import i18next from "i18next";
import { Wallet } from "lucide-react";
import { lazy, Suspense } from "react";
import type { PluginUI } from "../../../src/plugin-ui-contract";
const Overview = lazy(() => import("./Overview"));
const ReportsCentre = lazy(() => import("./ReportsCentre"));
const Reports = lazy(() => import("./Reports"));
const Statutory = lazy(() => import("./Statutory"));
const Structures = lazy(() => import("./Structures"));
const Compensation = lazy(() => import("./Compensation"));
const Payslip = lazy(() => import("./Payslip"));
const Operations = lazy(() => import("./Operations"));
const Readiness = lazy(() => import("./Readiness"));
const Payroll = lazy(() => import("./Payroll"));
function OperationsLoading() {
  const { Loading } = (window as unknown as { CARE_UI: PluginUI }).CARE_UI;
  return (
    <div className="px-3 md:px-6">
      <Loading />
    </div>
  );
}
function Route({
  facilityId,
  section,
  runId,
  employeeId,
}: {
  facilityId: string;
  section:
    | "payslip"
    | "preparation"
    | "runs"
    | "settings"
    | "compensation"
    | "structures"
    | "statutory";
  runId?: string;
  employeeId?: string;
}) {
  const { Loading } = (window as unknown as { CARE_UI: PluginUI }).CARE_UI;
  return (
    <Suspense
      fallback={
        <div className="px-3 md:px-6">
          <Loading />
        </div>
      }
    >
      {section === "payslip" ? (
        <Payslip
          facilityId={facilityId}
          runId={runId!}
          employeeId={employeeId!}
        />
      ) : section === "statutory" ? (
        <Statutory facilityId={facilityId} />
      ) : section === "structures" ? (
        <Structures facilityId={facilityId} />
      ) : section === "compensation" ? (
        <Compensation
          key={employeeId || "directory"}
          facilityId={facilityId}
          employeeId={employeeId}
        />
      ) : (
        <Payroll
          key={`${facilityId}/${section}/${runId || ""}`}
          facilityId={facilityId}
          section={section}
          runId={runId}
        />
      )}
    </Suspense>
  );
}
export default {
  plugin: "macare_payroll_fe",
  routes: {
    "/facility/:facilityId/payroll/reports/:reportId": ({
      facilityId,
      reportId,
    }: {
      facilityId: string;
      reportId: string;
    }) => (
      <Suspense fallback={<OperationsLoading />}>
        <Reports
          key={`${facilityId}/${reportId}`}
          facilityId={facilityId}
          reportId={reportId}
        />
      </Suspense>
    ),
    "/facility/:facilityId/payroll/reports": ({
      facilityId,
    }: {
      facilityId: string;
    }) => (
      <Suspense fallback={<OperationsLoading />}>
        <ReportsCentre key={facilityId} facilityId={facilityId} />
      </Suspense>
    ),
    "/facility/:facilityId/payroll/readiness": ({
      facilityId,
    }: {
      facilityId: string;
    }) => (
      <Suspense fallback={<OperationsLoading />}>
        <Readiness facilityId={facilityId} />
      </Suspense>
    ),
    ...Object.fromEntries(
      ["payments", "loans", "arrears", "settlements", "claims", "me"].flatMap(
        (section) => [
          [
            `/facility/:facilityId/payroll/${section}`,
            ({ facilityId }: { facilityId: string }) => (
              <Suspense fallback={<OperationsLoading />}>
                <Operations
                  facilityId={facilityId}
                  section={
                    section as
                      | "payments"
                      | "loans"
                      | "arrears"
                      | "settlements"
                      | "claims"
                      | "me"
                  }
                />
              </Suspense>
            ),
          ],
          [
            `/facility/:facilityId/payroll/${section}/:id`,
            ({ facilityId, id }: { facilityId: string; id: string }) => (
              <Suspense fallback={<OperationsLoading />}>
                <Operations
                  facilityId={facilityId}
                  id={id}
                  section={
                    section as
                      | "payments"
                      | "loans"
                      | "arrears"
                      | "settlements"
                      | "claims"
                      | "me"
                  }
                />
              </Suspense>
            ),
          ],
        ],
      ),
    ),
    "/facility/:facilityId/payroll/me/payslips/:runId/:employeeId": ({
      facilityId,
      runId,
      employeeId,
    }: {
      facilityId: string;
      runId: string;
      employeeId: string;
    }) => (
      <Suspense fallback={<OperationsLoading />}>
        <Payslip
          facilityId={facilityId}
          runId={runId}
          employeeId={employeeId}
          personal
        />
      </Suspense>
    ),

    "/facility/:facilityId/payroll/runs/:runId/payslips/:employeeId": ({
      facilityId,
      runId,
      employeeId,
    }: {
      facilityId: string;
      runId: string;
      employeeId: string;
    }) => (
      <Route
        facilityId={facilityId}
        runId={runId}
        employeeId={employeeId}
        section="payslip"
      />
    ),
    "/facility/:facilityId/payroll/compensation/:employeeId": ({
      facilityId,
      employeeId,
    }: {
      facilityId: string;
      employeeId: string;
    }) => (
      <Route
        facilityId={facilityId}
        section="compensation"
        employeeId={employeeId}
      />
    ),
    "/facility/:facilityId/payroll/monthly": ({
      facilityId,
    }: {
      facilityId: string;
    }) => <Route facilityId={facilityId} section="preparation" />,
    "/facility/:facilityId/payroll/statutory": ({
      facilityId,
    }: {
      facilityId: string;
    }) => <Route facilityId={facilityId} section="statutory" />,
    "/facility/:facilityId/payroll/structures": ({
      facilityId,
    }: {
      facilityId: string;
    }) => <Route facilityId={facilityId} section="structures" />,
    "/facility/:facilityId/payroll/compensation": ({
      facilityId,
    }: {
      facilityId: string;
    }) => <Route facilityId={facilityId} section="compensation" />,
    "/facility/:facilityId/payroll": ({
      facilityId,
    }: {
      facilityId: string;
    }) => <Route facilityId={facilityId} section="preparation" />,
    "/facility/:facilityId/payroll/runs": ({
      facilityId,
    }: {
      facilityId: string;
    }) => <Route facilityId={facilityId} section="runs" />,
    "/facility/:facilityId/payroll/runs/:runId": ({
      facilityId,
      runId,
    }: {
      facilityId: string;
      runId: string;
    }) => <Route facilityId={facilityId} section="runs" runId={runId} />,
    "/facility/:facilityId/payroll/settings": ({
      facilityId,
    }: {
      facilityId: string;
    }) => <Route facilityId={facilityId} section="settings" />,
  },
  navItems: [
    {
      name: i18next.t("payroll_nav", "Payroll") || "Payroll",
      url: "payroll",
      icon: <Wallet />,
      children: [
        { name: "My payroll", url: "me" },
        { name: "Payroll readiness", url: "readiness" },
        { name: "Employee compensation", url: "compensation" },
        { name: "Monthly payroll", url: "monthly" },
        { name: "Payroll runs", url: "runs" },
        { name: "Salary payments", url: "payments" },
        { name: "Loans and advances", url: "loans" },
        { name: "Expense claims", url: "claims" },
        { name: "Salary arrears", url: "arrears" },
        { name: "Final settlements", url: "settlements" },
        { name: "Reports", url: "reports" },
        { name: "Salary structures", url: "structures" },
        { name: "Statutory settings", url: "statutory" },
        { name: "Payroll settings", url: "settings" },
      ].map((item) => ({
        name:
          i18next.t(`payroll:${item.name}`, { defaultValue: item.name }) ||
          item.name,
        url: `payroll/${item.url}`,
      })),
    },
  ],
  extends: [],
  components: { FacilityOverviewSections: Overview },
  devices: [],
};
